﻿import React, { useEffect, useState } from 'react';

import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { getMessageFromPromiseReject } from 'utils/other';
import { FormattedMessage, useIntl } from 'react-intl';
import { Student, StudentApi } from 'apis/StudentApi';
import { Col, Container, Row } from 'react-bootstrap';
import { StudentGroup } from 'apis/StudentGroupApi';
import { User, UsersApi } from 'apis/UsersApi';
import { passMeetsRequirements } from 'utils';

import 'components/ResetPasswordDialog/resetPasswordDialog.scss';

interface GroupsEntities {
    data: StudentGroup[];
    type: 'groups';
}

interface StudentsEntities {
    data: Student[];
    type: 'students';
}

interface UsersEntities {
    data: User[];
    type: 'users';
}

interface ResetPasswordButtonProps {
    entities: GroupsEntities | StudentsEntities | UsersEntities;
    onCancel: () => void;
    onError: (message: string) => void;
    onSuccess: (message: string) => void;
    show: boolean;
}

export function ResetPasswordDialog({ entities, onCancel, onError, onSuccess, show }: ResetPasswordButtonProps) {

    const [error, setError] = useState(false);
    const [newPassword, setNewPassword] = useState<string>('');
    const { formatMessage } = useIntl();

    useEffect(() => {

        if (!show) {

            setError(false);
            setNewPassword('');

        }

    }, [show]);

    const resetPasswords = () => {

        switch (entities.type) {

            case 'students':
                StudentApi.BulkResetPassword(
                    entities.data.map(student => student.Id),
                    newPassword
                )
                    .then(response => onSuccess(JSON.parse(response)[0].Message))
                    .catch(reason => {

                        const message = getMessageFromPromiseReject(reason);
                        onError(message);

                    });
                break;
            case 'users':
                UsersApi.ResetAdminPassword(entities.data[0].Id, newPassword)
                    .then(response => onSuccess(JSON.parse(response)[0].Message))
                    .catch(reason => {

                        const message = getMessageFromPromiseReject(reason);
                        onError(message);

                    });
                break;

        }

    };

    const setPasswordMessage = () => {

        if (entities.type === 'users') {

            return formatMessage({ id: 'set_the_default_password_for_this_user_t.953ba29a', defaultMessage: 'Set the default password for this user to:' });

        } else if (entities.data.length > 1) {

            return (
                <div>
                    {entities.type === 'students' ? (
                        <FormattedMessage
                            values={{
                                b: chunks => (
                                    <span className="font-weight-bold">
                                        {chunks}
                                    </span>
                                ),
                                count: entities.data.length
                            }} defaultMessage="Set the default password for the <b>{count}</b> selected students to:"
                            id="set_the_default_password_for_the_count_s.e27e5579"
                        />
                    ) : (
                        <FormattedMessage
                            values={{
                                b: chunks => (
                                    <span className="font-weight-bold">
                                        {chunks}
                                    </span>
                                ),
                                count: entities.data.length
                            }} defaultMessage="Set the default password for all students in the <b>{count}</b> selected groups to:"
                            id="set_the_default_password_for_all_student.1977a769"
                        />
                    )}
                </div>
            );

        } else {

            return entities.type === 'students'
                ? formatMessage({ id: 'set_the_default_password_for_the_selecte.0e7a4f92', defaultMessage: 'Set the default password for the selected student to:' })
                : formatMessage({ id: 'set_the_default_password_for_all_student.33695d5b', defaultMessage: 'Set the default password for all students in the selected group to:' });

        }

    };

    const onConfirmClick = () => {

        setError(false);
        if (passMeetsRequirements(newPassword)) {

            resetPasswords();

        } else {

            setError(true);

        }

    };

    return (
        <>
            <GMetrixDialog
                actionButtonClick={onConfirmClick}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'reset_password.9c0200aa', defaultMessage: 'Reset Password' })}
                dialogLabel={formatMessage({ id: 'reset_password.9c0200aa', defaultMessage: 'Reset Password' })}
                onCancel={onCancel}
                show={show}
                size="lg"
            >
                <Container>
                    <Row className="d-flex flex-column align-items-center">
                        <Col className="d-flex justify-content-center col-12 reset-dialog-container__prompt">
                            {setPasswordMessage()}
                        </Col>
                        <Col className="col-12" md={6}>
                            <GMetrixInput
                                label={!error
                                    ? formatMessage({ id: 'password.eb68d5ea', defaultMessage: 'Password', description: 'Formulario' })
                                    : formatMessage({ id: 'password_please_refer_to_password_requir.a94d07a4', defaultMessage: 'Password - Please refer to password requirements' })}
                                onChange={e => setNewPassword(e.target.value)} value={newPassword}
                            />
                        </Col>
                        <Col className="my-3 col-12 text-align-center" md={6}>
                            <b>
                                <FormattedMessage defaultMessage="Password requirements:" id="password_requirements.6aaa2f45" />
                                {' '}
                                {' '}
                            </b>
                            {' '}
                            <FormattedMessage defaultMessage="minimum 8 characters, at least one uppercase letter, one lowercase, one number, and one special character." id="minimum_8_characters_at_least_one_upperc.2ba9707a" />
                        </Col>
                    </Row>
                </Container>
            </GMetrixDialog>
        </>
    );

}
